import type { SettingsDoc } from "@/content";
import { organisationSettings } from "@/data/organisation";
import { siteUrl } from "@/data/site";
import { absoluteUrl } from "@/lib/site-url";
import type { Faq } from "@/data/faqs";

/**
 * Structured data, generated from the CMS.
 *
 * One rule governs every function here: a property is emitted only when there
 * is a real, confirmed value behind it. Nothing is padded to make a node look
 * complete. That is not fastidiousness, it is the same fact policy the rest of
 * the site follows, applied to the one audience that cannot ask a follow-up
 * question. A search engine reads `identifier` as a claim about a legal entity
 * and `Person` as a claim about somebody who exists, and both get repeated back
 * to people in ways nobody at HISCUS will ever see.
 *
 * So:
 *   an unconfirmed address       omits streetAddress rather than guessing
 *   an unverified registration   is shown on the page and left out of the graph
 *   a person with no public bio  produces no Person node at all
 *   a video with no valid URL    produces no VideoObject
 *   no payment destination       produces no DonateAction
 *
 * Every function takes settings and falls back to the committed snapshot, so a
 * render that happens before the settings document exists still produces a
 * correct graph rather than an empty one.
 */

const fallbackSettings = organisationSettings as unknown as SettingsDoc;

/** Drops every key whose value is empty, so no node carries a blank property. */
function compact<T extends Record<string, unknown>>(node: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out as T;
}

/** Organisation identity, emitted once from the root layout. */
export function organizationJsonLd(settings: SettingsDoc = fallbackSettings) {
  const address = compact({
    "@type": "PostalAddress",
    addressCountry: settings.addressCountry || "NP",
    addressLocality: settings.addressLocality || "Kathmandu",
    addressRegion: settings.addressRegion,
    postalCode: settings.postalCode,
    // Published only where HISCUS has published it. See the note above.
    streetAddress: settings.address?.confirmed ? settings.address.value : undefined,
  });

  /*
   * Only a verified identifier reaches the graph.
   *
   * All three registration numbers are published on the organisation's own
   * About page and all three are shown on this site. `verified` means somebody
   * has checked them against a certificate, and until that has happened they
   * are a published claim rather than a checked one, which is a distinction
   * worth keeping when the consumer is a machine.
   */
  const identifiers = (settings.registrations ?? [])
    .filter((r) => r.verified && r.value)
    .map((r) => ({ "@type": "PropertyValue", name: r.label, value: r.value }));

  /** A contact point needs something a person can actually contact. */
  const contactPoints = [
    settings.email?.confirmed && settings.email.value
      ? compact({
          "@type": "ContactPoint",
          contactType: "general enquiries",
          email: settings.email.value,
          telephone: settings.phone?.confirmed ? settings.phone.value : undefined,
          areaServed: "NP",
          availableLanguage: ["en", "ne"],
        })
      : null,
  ].filter(Boolean);

  return compact({
    "@context": "https://schema.org",
    "@type": "NGO",
    "@id": `${siteUrl}/#organization`,
    name: settings.legalName,
    alternateName: settings.shortName,
    url: siteUrl,
    logo: absoluteUrl("/icon.png"),
    // The year, not a constructed date. HISCUS publishes 1998 and not the day.
    foundingDate: settings.foundingDate || (settings.establishedYear ? String(settings.establishedYear) : undefined),
    description: settings.mission,
    slogan: settings.motto,
    areaServed: { "@type": "Country", name: "Nepal" },
    address,
    email: settings.email?.confirmed ? settings.email.value : undefined,
    telephone: settings.phone?.confirmed ? settings.phone.value : undefined,
    contactPoint: contactPoints.length ? contactPoints : undefined,
    identifier: identifiers.length ? identifiers : undefined,
    sameAs: (settings.socialLinks ?? []).map((s) => s.href),
  });
}

export function websiteJsonLd(settings: SettingsDoc = fallbackSettings) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    url: siteUrl,
    name: settings.legalName,
    inLanguage: "en",
    publisher: { "@id": `${siteUrl}/#organization` },
  };
}

/**
 * One page.
 *
 * `AboutPage` and `ContactPage` are used only where the page really is one, and
 * the type comes from the CMS rather than from a guess about the URL. A wrong
 * page type is worse than none: it tells a search engine to read the page as
 * something it is not.
 */
export function webPageJsonLd({
  title,
  description,
  path,
  type = "WebPage",
  settings = fallbackSettings,
}: {
  title: string;
  description?: string;
  path: string;
  type?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage";
  settings?: SettingsDoc;
}) {
  return compact({
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${absoluteUrl(path)}#webpage`,
    url: absoluteUrl(path),
    name: title,
    description,
    isPartOf: { "@id": `${siteUrl}/#website` },
    about: { "@id": `${siteUrl}/#organization` },
    publisher: { "@id": `${siteUrl}/#organization` },
    inLanguage: "en",
    ...(settings.legalName ? {} : {}),
  });
}

export function breadcrumbJsonLd(trail: { name: string; href: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.href),
    })),
  };
}

export function articleJsonLd({
  title,
  description,
  path,
  isoDate,
  modifiedIso,
  authorName,
  type = "Article",
  imageUrl,
  organisationName = fallbackSettings.legalName,
}: {
  title: string;
  description: string;
  path: string;
  isoDate: string;
  modifiedIso?: string;
  /** A real person, or empty. An invented byline is a claim about somebody. */
  authorName?: string;
  type?: "Article" | "NewsArticle";
  imageUrl?: string;
  organisationName?: string;
}) {
  return compact({
    "@context": "https://schema.org",
    "@type": type,
    headline: title,
    description,
    datePublished: isoDate,
    dateModified: modifiedIso && !Number.isNaN(Date.parse(modifiedIso)) ? modifiedIso : undefined,
    mainEntityOfPage: absoluteUrl(path),
    image: imageUrl ? absoluteUrl(imageUrl) : undefined,
    publisher: { "@id": `${siteUrl}/#organization` },
    author: authorName
      ? { "@type": "Person", name: authorName }
      : { "@type": "Organization", name: organisationName },
  });
}

/**
 * A video, and only a real one.
 *
 * Called from the one place that has already parsed the URL into an embed, so
 * there is no path by which an empty or unrecognised address produces a node.
 * A VideoObject describing a video that does not exist is a rich result that
 * takes somebody to nothing.
 */
export function videoJsonLd({
  name,
  description,
  uploadDate,
  embedUrl,
  watchUrl,
  thumbnailUrl,
  durationSeconds,
}: {
  name: string;
  description?: string;
  uploadDate: string;
  embedUrl: string;
  watchUrl: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
}) {
  return compact({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    description,
    uploadDate,
    embedUrl,
    contentUrl: watchUrl,
    thumbnailUrl: thumbnailUrl ? absoluteUrl(thumbnailUrl) : undefined,
    duration: durationSeconds ? `PT${Math.round(durationSeconds)}S` : undefined,
    publisher: { "@id": `${siteUrl}/#organization` },
  });
}

/** A photograph, with the description somebody actually wrote for it. */
export function imageJsonLd({
  url,
  caption,
  width,
  height,
  credit,
}: {
  url: string;
  caption?: string;
  width?: number;
  height?: number;
  credit?: string;
}) {
  return compact({
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: absoluteUrl(url),
    caption,
    width,
    height,
    creditText: credit,
  });
}

/**
 * Leadership.
 *
 * Emitted only for people whose name and role HISCUS has published. There is no
 * leadership list yet, so this currently returns nothing, which is the correct
 * output: a Person node is a claim that a named individual holds a named role.
 */
export function personJsonLd(people: { name: string; role?: string; confirmed?: boolean }[]) {
  const confirmed = people.filter((p) => p.confirmed !== false && p.name);
  if (confirmed.length === 0) return null;
  return confirmed.map((person) =>
    compact({
      "@context": "https://schema.org",
      "@type": "Person",
      name: person.name,
      jobTitle: person.role,
      worksFor: { "@id": `${siteUrl}/#organization` },
    }),
  );
}

/**
 * A donate action, and only with somewhere to donate.
 *
 * There is no verified payment destination, so this returns null and the graph
 * says nothing about donating. A DonateAction pointing at a contact form tells
 * a search engine the organisation takes donations online, which it does not.
 */
export function donateActionJsonLd(settings: SettingsDoc = fallbackSettings) {
  const support = settings.support;
  if (!support?.donationEnabled || !support.donationUrl) return null;
  return {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    recipient: { "@id": `${siteUrl}/#organization` },
    target: { "@type": "EntryPoint", urlTemplate: support.donationUrl },
  };
}

/** Only ever called from a page that renders the same questions visibly. */
export function faqJsonLd(faqs: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}
